import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Settings, Palette, Image, Type, Sun, Moon, Save } from 'lucide-react';

const SettingsPage = () => {
  const { token } = useAuth();
  const { settings, updateSettings, refreshSettings } = useSettings();
  const [formData, setFormData] = useState({
    school_name: '',
    logo_url: '',
    accent_color: '#2563EB',
    default_theme: 'dark'
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setFormData({
        school_name: settings.school_name || '',
        logo_url: settings.logo_url || '',
        accent_color: settings.accent_color || '#2563EB',
        default_theme: settings.default_theme || 'dark'
      });
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(formData, token);
      toast.success('Settings saved successfully! Refresh to see changes.');
      refreshSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const presetColors = [
    { name: 'Blue', value: '#2563EB' },
    { name: 'Red', value: '#DC2626' },
    { name: 'Green', value: '#16A34A' },
    { name: 'Purple', value: '#9333EA' },
    { name: 'Orange', value: '#EA580C' },
    { name: 'Teal', value: '#0D9488' },
    { name: 'Pink', value: '#DB2777' },
    { name: 'Indigo', value: '#4F46E5' },
  ];

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">Customize your dashboard appearance and branding</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Branding Settings */}
        <Card className="border-border/50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Type className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg">Branding</CardTitle>
            </div>
            <CardDescription>Customize your school name and logo</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="school_name">School Name</Label>
              <Input
                id="school_name"
                data-testid="settings-school-name"
                value={formData.school_name}
                onChange={(e) => setFormData({ ...formData, school_name: e.target.value })}
                placeholder="Enter your school name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="logo_url">Logo URL</Label>
              <Input
                id="logo_url"
                data-testid="settings-logo-url"
                value={formData.logo_url}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                placeholder="https://example.com/logo.png"
              />
              {formData.logo_url && (
                <div className="mt-3 p-4 bg-muted rounded-lg flex items-center justify-center">
                  <img 
                    src={formData.logo_url} 
                    alt="Logo Preview" 
                    className="max-h-20 max-w-full object-contain"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Appearance Settings */}
        <Card className="border-border/50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg">Appearance</CardTitle>
            </div>
            <CardDescription>Set accent color and default theme</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Accent Color</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  data-testid="settings-color-picker"
                  value={formData.accent_color}
                  onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer border border-input"
                />
                <Input
                  value={formData.accent_color}
                  onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                  placeholder="#2563EB"
                  className="flex-1 font-mono"
                />
              </div>
              
              {/* Preset Colors */}
              <div className="flex flex-wrap gap-2 mt-3">
                {presetColors.map((color) => (
                  <button
                    key={color.value}
                    onClick={() => setFormData({ ...formData, accent_color: color.value })}
                    className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                      formData.accent_color === color.value ? 'border-foreground scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  />
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Default Theme</Label>
              <Select 
                value={formData.default_theme} 
                onValueChange={(v) => setFormData({ ...formData, default_theme: v })}
              >
                <SelectTrigger data-testid="settings-theme-select">
                  <SelectValue placeholder="Select theme" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dark">
                    <div className="flex items-center gap-2">
                      <Moon className="w-4 h-4" />
                      Dark Mode
                    </div>
                  </SelectItem>
                  <SelectItem value="light">
                    <div className="flex items-center gap-2">
                      <Sun className="w-4 h-4" />
                      Light Mode
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Preview Card */}
      <Card className="border-border/50">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Image className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">Preview</CardTitle>
          </div>
          <CardDescription>See how your branding will look</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-900 rounded-xl p-6 flex items-center gap-4">
            {formData.logo_url ? (
              <img 
                src={formData.logo_url} 
                alt="Logo" 
                className="w-12 h-12 rounded-xl object-contain bg-white p-1"
                onError={(e) => e.target.style.display = 'none'}
              />
            ) : (
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: formData.accent_color }}
              >
                <Settings className="w-6 h-6" />
              </div>
            )}
            <div>
              <h3 className="text-white font-bold text-lg">{formData.school_name || 'School Name'}</h3>
              <p className="text-slate-400 text-sm">AV Equipment Inventory</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          data-testid="save-settings-btn"
          onClick={handleSave} 
          disabled={saving}
          size="lg"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

export default SettingsPage;
